import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, TextField, Button, MenuItem, Chip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  CircularProgress, Alert, Divider,
} from '@mui/material';
import { Payments as TakeIcon } from '@mui/icons-material';
import API from '../../BaseURL';
import swal from '../../utils/swal';

// ═══════════════════════════════════════════════════════════════════
//  Taking an advance payment for ONE student.
//
//  The guts of it, with no chrome, so the same form can be a page at
//  the accounts desk and a dialog opened straight off a student's row
//  in Approve Collections — which is where somebody actually is when a
//  parent says "let me pay the rest of the term". Walking them to
//  another screen to do it is how a payment gets taken against the
//  wrong learner.
//
//  The form's real job is the QUOTE. A parent does not say "GH¢ 504",
//  they say "the whole term", so it counts the school days left and
//  shows the working — checkable in a way a lump sum is not.
// ═══════════════════════════════════════════════════════════════════

const TNUM = { fontVariantNumeric: 'tabular-nums' };
const money = (n) => Number(n || 0).toLocaleString(undefined,
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const prettyType = (t) => String(t || '')
  .replace(/_+$/, '').replace(/_/g, ' ')
  .replace(/\b\w/g, (c) => c.toUpperCase());

const isoToday = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const longDate = (d) => /^\d{4}-\d{2}-\d{2}$/.test(String(d || '').slice(0, 10))
  ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString(undefined,
      { day: 'numeric', month: 'long', year: 'numeric' })
  : (d || '');

export const COVERS = [
  { key: 'term',   label: 'Rest of the term' },
  { key: 'month',  label: 'Rest of this month' },
  { key: 'year',   label: 'Rest of the year' },
  { key: 'days',   label: 'A number of days' },
  { key: 'amount', label: 'A fixed amount' },
];

export default function PrepaymentForm({
  studentId, studentName, className, incomeType, compact = false, onDone,
}) {
  const [cover, setCover] = useState('term');
  const [days, setDays] = useState(20);
  const [amount, setAmount] = useState('');
  const [from, setFrom] = useState(isoToday);
  const [chosen, setChosen] = useState(incomeType ? [incomeType] : []);
  const [accounts, setAccounts] = useState([]);
  const [bank, setBank] = useState('');
  const [mode, setMode] = useState('Cash');
  const [reference, setReference] = useState('');

  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [taking, setTaking] = useState(false);
  const [error, setError] = useState('');

  // The same account list the cashier desk uses, so an account that
  // works on Day Payment works here.
  useEffect(() => {
    API.get('/payments/bank-accounts').then(r => {
      const rows = Array.isArray(r.data) ? r.data : (r.data?.rows || []);
      setAccounts(rows);
      const nameOf = (a) => a.na || a.name || a.account_name || '';
      const cash = rows.find(a => /cash/i.test(nameOf(a)));
      setBank(nameOf(cash || rows[0] || {}));
    }).catch(() => setAccounts([]));
  }, []);

  useEffect(() => { setChosen(incomeType ? [incomeType] : []); }, [incomeType, studentId]);

  const getQuote = useCallback(async () => {
    if (!studentId) { setQuote(null); return; }
    setQuoting(true); setError('');
    try {
      const r = await API.post('/daily-subscriptions/prepayments/quote', {
        student_id: studentId,
        cover, from_date: from,
        ...(cover === 'days' ? { days: Number(days) || 1 } : {}),
        ...(cover === 'amount' ? { amount: Number(amount) || 0 } : {}),
        ...(chosen.length ? { income_types: chosen } : {}),
      });
      setQuote(r.data || null);
    } catch (e) {
      setQuote(null);
      setError(e?.response?.data?.message || 'Could not work out what this would cost');
    } finally { setQuoting(false); }
  }, [studentId, cover, from, days, amount, chosen]);

  // Debounced — the amount and day boxes are typed into.
  useEffect(() => {
    const t = setTimeout(getQuote, 350);
    return () => clearTimeout(t);
  }, [getQuote]);

  const lines = useMemo(() => quote?.lines || [], [quote]);

  const take = async () => {
    if (!quote || taking) return;
    if (!bank) { swal.warning('Choose the account the money went into.'); return; }
    if (quote.total <= 0) { swal.warning('The quote comes to nothing.'); return; }

    const ok = await swal.confirm({
      title: 'Take this payment?',
      tone: 'success',
      html: `<b>${studentName || studentId}</b>${className ? ` · ${className}` : ''}<br/>`
          + `${quote.covers_label}${quote.covers_to ? ` — to ${longDate(quote.covers_to)}` : ''}`
          + `<br/><br/><b>GH¢ ${money(quote.total)}</b>`
          + (quote.arrears_total > 0
              ? `<br/><span style="opacity:.8">GH¢ ${money(quote.arrears_total)} clears what is owed, `
                + `GH¢ ${money(quote.advance_total)} goes on account</span>`
              : '<br/><span style="opacity:.8">held on account and spent day by day</span>')
          + '<br/><br/>This posts to the cash book immediately and issues a receipt. '
          + 'It does not go to a teacher for collection.',
      confirmText: 'Yes, take it',
    });
    if (!ok) return;

    setTaking(true);
    try {
      const r = await API.post('/daily-subscriptions/prepayments', {
        student_id: studentId,
        cover, from_date: from,
        ...(cover === 'days' ? { days: Number(days) || 1 } : {}),
        ...(cover === 'amount' ? { amount: Number(amount) || 0 } : {}),
        ...(chosen.length ? { income_types: chosen } : {}),
        payment_date: from,
        mode, bank_account: bank, reference_no: reference,
      });
      swal.success(r.data?.message || 'Payment taken');
      setReference(''); setQuote(null);
      onDone?.(r.data);
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not take the payment'));
    } finally { setTaking(false); }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 1.4, fontSize: 12 }}>{error}</Alert>}

      {/* ── How long ────────────────────────────────────────────── */}
      <Stack direction="row" spacing={1.2} sx={{ mb: 1.4 }} flexWrap="wrap" useFlexGap>
        <TextField size="small" type="date" label="Cover from" value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ minWidth: 168 }} />
        <TextField select size="small" label="Cover" value={cover}
          onChange={(e) => setCover(e.target.value)} sx={{ minWidth: 176 }}>
          {COVERS.map(c => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}
        </TextField>
        {cover === 'days' && (
          <TextField size="small" type="number" label="School days" value={days}
            onChange={(e) => setDays(e.target.value)}
            inputProps={{ min: 1, max: 400 }} sx={{ width: 140 }} />
        )}
        {cover === 'amount' && (
          <TextField size="small" type="number" label="Amount (GH¢)" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }} sx={{ width: 160 }} />
        )}
      </Stack>

      {/* Which collections. All of them unless the parent says
          otherwise — paying feeding but not transport is a choice, and
          has to be an explicit one. */}
      {lines.length > 1 && (
        <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mb: 1.4 }}>
          {lines.map(l => {
            const on = !chosen.length || chosen.includes(l.income_type);
            return (
              <Chip key={l.income_type} size="small" clickable
                label={`${prettyType(l.income_type)} · ${money(l.daily_amount)}/day`}
                onClick={() => setChosen(c => {
                  const all = lines.map(x => x.income_type);
                  const cur = c.length ? c : all;
                  const next = cur.includes(l.income_type)
                    ? cur.filter(x => x !== l.income_type)
                    : [...cur, l.income_type];
                  if (!next.length) return c;          // never none
                  return next.length === all.length ? [] : next;
                })}
                sx={{
                  height: 25, fontSize: 10.5, fontWeight: 700,
                  bgcolor: on ? '#eef4fb' : '#f8fafc',
                  color: on ? '#1c5b96' : '#94a3b8',
                  border: `1px solid ${on ? '#2f6fb0' : '#e2e8f0'}`,
                }} />
            );
          })}
        </Stack>
      )}

      {/* ── What it comes to ────────────────────────────────────── */}
      {quoting && !quote && (
        <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>
      )}

      {quote && (
        <>
          <Typography sx={{ fontSize: 11.5, color: '#64748b', mb: 0.8 }}>
            {quote.covers_label}
            {quote.covers_to && <> — to <b>{longDate(quote.covers_to)}</b></>}
          </Typography>

          <TableContainer sx={{ mb: 1.4 }}>
            <Table size="small" sx={{
              '& th': { bgcolor: '#f3f7fc', color: '#334a6b', fontWeight: 800,
                        fontSize: 10.5, py: 0.7 },
              '& td': { fontSize: 11.5, py: 0.55, borderBottom: '1px solid #eef2f7' },
            }}>
              <TableHead>
                <TableRow>
                  <TableCell>Collection</TableCell>
                  <TableCell align="center">Days</TableCell>
                  <TableCell align="right">Rate</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map(l => (
                  <TableRow key={l.income_type}>
                    <TableCell>
                      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#0f172a' }}>
                        {prettyType(l.income_type)}
                      </Typography>
                      {/* Days the money already on account covers — so
                          the desk never charges twice for them. */}
                      {l.already_covered_days > 0 && (
                        <Typography sx={{ fontSize: 9.5, color: '#4338ca', fontWeight: 700 }}>
                          {l.already_covered_days} day{l.already_covered_days === 1 ? '' : 's'}
                          {' '}already on account
                        </Typography>
                      )}
                      {l.arrears > 0 && (
                        <Typography sx={{ fontSize: 9.5, color: '#b91c1c', fontWeight: 700 }}>
                          GH¢ {money(l.arrears)} owed — cleared first
                        </Typography>
                      )}
                      {l.last_day && (
                        <Typography sx={{ fontSize: 9.5, color: '#64748b' }}>
                          covers to {longDate(l.last_day)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ ...TNUM, fontWeight: 700 }}>
                      {l.charge_days}
                    </TableCell>
                    <TableCell align="right" sx={TNUM}>{money(l.daily_amount)}</TableCell>
                    <TableCell align="right" sx={{ ...TNUM, fontWeight: 800 }}>
                      {money(l.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{
            px: 1.6, py: 1.2, borderRadius: 2, mb: 1.4,
            bgcolor: '#f1faf5', border: '1px solid #cfe7d6',
          }}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#15803d' }}>
                Total to collect
              </Typography>
              <Typography sx={{ fontSize: 21, fontWeight: 800, color: '#15803d', ...TNUM }}>
                GH¢ {money(quote.total)}
              </Typography>
            </Stack>
            {quote.arrears_total > 0 && (
              <Typography sx={{ fontSize: 10.5, color: '#64748b', mt: 0.3 }}>
                GH¢ {money(quote.arrears_total)} clears what is owed ·
                GH¢ {money(quote.advance_total)} goes on account
              </Typography>
            )}
          </Box>
        </>
      )}

      {!compact && <Divider sx={{ mb: 1.4 }} />}

      {/* ── How it was paid ─────────────────────────────────────── */}
      <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap sx={{ mb: 1.6 }}>
        <TextField select size="small" label="Account" value={bank}
          onChange={(e) => setBank(e.target.value)} sx={{ minWidth: 180 }}>
          {accounts.length === 0 && <MenuItem value="">—</MenuItem>}
          {accounts.map(a => {
            const n = a.na || a.name || a.account_name;
            return <MenuItem key={n} value={n}>{n}</MenuItem>;
          })}
        </TextField>
        <TextField select size="small" label="Mode" value={mode}
          onChange={(e) => setMode(e.target.value)} sx={{ minWidth: 126 }}>
          {['Cash', 'Momo', 'Cheque', 'Bank Transfer'].map(m => (
            <MenuItem key={m} value={m}>{m}</MenuItem>
          ))}
        </TextField>
        <TextField size="small" label="Reference (optional)" value={reference}
          onChange={(e) => setReference(e.target.value)} sx={{ minWidth: 160, flex: 1 }} />
      </Stack>

      <Button fullWidth variant="contained" disableElevation
        disabled={taking || !quote || quote.total <= 0}
        onClick={take}
        startIcon={taking
          ? <CircularProgress size={16} color="inherit" />
          : <TakeIcon />}
        sx={{
          textTransform: 'none', fontWeight: 700, fontSize: 14.5,
          py: 1.3, borderRadius: 2,
          bgcolor: '#22a559', '&:hover': { bgcolor: '#15803d' },
        }}>
        {quote
          ? `Take GH¢ ${money(quote.total)} and issue a receipt`
          : 'Take payment'}
      </Button>
    </Box>
  );
}
